import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import styles from './Pagination.module.scss.js';
import { ChevronLeft, ChevronRight } from '@livingdesign/icons';
import '@walmart-web/livingdesign-components/index.esm.css';
import '@walmart-web/livingdesign-components';
import '../Alert/Alert.js';
import '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import '../Tag/Tag.js';
import '../Checkbox/Checkbox.js';
import '../Chip/Chip.js';
import classNames from '../../node_modules/classnames/index.js';
import { IconButton } from '../IconButton/IconButton.js';
import '@livingdesign/tokens/dist/js/light/regular/components/Form';
import '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import '../SearchBox/SearchBox.js';
import '../AutoComplete/AutoComplete.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../utility/constants.js';
import { Select } from '../LuminateSelect/LuminateSelect.js';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Table/Table.js';
import '@react-aria/ssr';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';
import pagination from '../utility/pagination.js';

/**
 * The pagination component enables the user to select a specific page from a range of pages. 
 * Pagination is used in conjunction with a data table to view additional rows in the table, spread across multiple pages.
 *
 * ### Content
 *
 * The pagination component accepts the total count of the table items and calculates the number of pages based on that count.
 * The page number and items to be displayed on the page are sent to the callback function to perform necessary operations.
 * It also accepts two boolean props for hiding the items per page count and the items per page dropdown.
 *
 * ```tsx
  * import * as React from "react";
 * import { Pagination } from "@walmart-web/livingdesign-components";
 * 
 * export default () => {
 *  return (
 *    <Pagination
          currentPage={1}
          hideItemsPerPageCount
          hideItemsPerPageDropdown
          itemsPerPage={'5'}
          onItemsPerPageChange={(value) => { console.log('Do something with the selected value from dropdown', value)}}
          setCurrentPage={(value) => { console.log('Do something with the current page value', value)}}
          totalItemCount={50}
      />
 *  )
 * }
 * 
 * ```
 * 
 * ### Features
 * 
 * ### Variants
 * 
 * Pagination comes with four variants:
 * 
 * - Default: All pagination elements are visible by default, such as: Items per page dropdown, count and pagination view
 * - Items per page & Pagination: The items per page count is hidden in this variant
 * - Total item count & Pagination: The items per page dropdown is not displayed in this variant
 * - Pagination only: Items per page dropdown and count are not displayed in this variant
 * 
 * See code snippets in the stories below for implementation details, to trigger these variants.
 * 
 * Pagination with ellipsis
 * 
 * Page number display with ellipsis is implemented via an algorithm automatically. 
 * The algorithm ensures that first and last boundary of pages is always displayed, along with 2 page numbers before and after the current selected page.
 */

var Pagination = function Pagination(props) {
  var _props$currentPage = props.currentPage,
    currentPage = _props$currentPage === void 0 ? 1 : _props$currentPage,
    hideItemsPerPageCount = props.hideItemsPerPageCount,
    hideItemsPerPageDropdown = props.hideItemsPerPageDropdown,
    _props$itemsPerPage = props.itemsPerPage,
    itemsPerPage = _props$itemsPerPage === void 0 ? '5' : _props$itemsPerPage,
    onItemsPerPageChange = props.onItemsPerPageChange,
    setCurrentPage = props.setCurrentPage,
    totalItemCount = props.totalItemCount,
    _props$size = props.size,
    size = _props$size === void 0 ? 'medium' : _props$size;
  var parsedCurrentPage = Number(currentPage);
  var itemsPerPageOptions = ['5', '10', '15', '20'];
  var _React$useState = React.useState([]),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    totalPages = _React$useState2[0],
    setTotalPages = _React$useState2[1];
  var _React$useState3 = React.useState(''),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    itemBoundaryToDisplayOnCurrentPage = _React$useState4[0],
    setItemBoundaryToDisplayOnCurrentPage = _React$useState4[1];
  var _React$useState5 = React.useState([]),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    paginationView = _React$useState6[0],
    setPaginationView = _React$useState6[1];
  React.useEffect(function () {
    var pages = Math.ceil(totalItemCount / parseInt(itemsPerPage));
    var total = new Array(pages).fill(1).map(function (_, idx) {
      return idx + 1;
    });
    setTotalPages(total);
  }, [itemsPerPage, totalItemCount]);
  React.useEffect(function () {
    setPaginationView(pagination(parsedCurrentPage, totalPages));
  }, [parsedCurrentPage, itemsPerPage, totalPages]);
  React.useEffect(function () {
    if (parsedCurrentPage !== (totalPages === null || totalPages === void 0 ? void 0 : totalPages.length)) {
      setItemBoundaryToDisplayOnCurrentPage("".concat((parsedCurrentPage - 1) * parseInt(itemsPerPage) + 1, " - ").concat(parsedCurrentPage * parseInt(itemsPerPage), " of ").concat(totalItemCount, " items"));
    } else {
      setItemBoundaryToDisplayOnCurrentPage("".concat((parsedCurrentPage - 1) * parseInt(itemsPerPage) + 1, " - ").concat(totalItemCount, " of ").concat(totalItemCount, " items"));
    }
  }, [parsedCurrentPage, itemsPerPage, totalItemCount, totalPages === null || totalPages === void 0 ? void 0 : totalPages.length]);
  var onDropdownChange = function onDropdownChange(option) {
    onItemsPerPageChange === null || onItemsPerPageChange === void 0 || onItemsPerPageChange(option); // callback from parent
    setCurrentPage(1); // reset current page to 1 so item boundary to display can be recalculated
  };

  var ItemsPerPage = function ItemsPerPage() {
    return !hideItemsPerPageDropdown ? /*#__PURE__*/React.createElement(Select, {
      label: "Items per page:",
      onChange: onDropdownChange,
      size: size === 'small' ? 'small' : 'large',
      value: itemsPerPage,
      options: itemsPerPageOptions,
      horizontalLabel: true
    }) : null;
  };
  var TotalItemCount = function TotalItemCount() {
    return !hideItemsPerPageCount ? /*#__PURE__*/React.createElement("div", {
      className: styles.pageRangeDisplay,
      id: "totalCountTextId"
    }, itemBoundaryToDisplayOnCurrentPage) : null;
  };
  var PaginationList = function PaginationList() {
    return /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.lastCol, hideItemsPerPageCount && hideItemsPerPageDropdown ? styles.paginationAlignment : '')
    }, /*#__PURE__*/React.createElement(IconButton, {
      disabled: parsedCurrentPage === 1,
      a11yLabel: "Go to Previous Page",
      size: size,
      onClick: function onClick() {
        return setCurrentPage(parsedCurrentPage - 1);
      }
    }, /*#__PURE__*/React.createElement(ChevronLeft, null)), paginationView.map(function (page, index) {
      if (page === '...') {
        return /*#__PURE__*/React.createElement("span", {
          key: index,
          className: styles.dotIndicator
        }, "...");
      } else {
        return /*#__PURE__*/React.createElement(IconButton, {
          UNSAFE_className: parsedCurrentPage === page ? styles.selected : '',
          key: index,
          a11yLabel: "Page ".concat(page, " ").concat(parsedCurrentPage === page ? ', Current Page' : ''),
          size: size,
          onClick: function onClick() {
            return setCurrentPage(page);
          }
        }, page);
      }
    }), /*#__PURE__*/React.createElement(IconButton, {
      disabled: parsedCurrentPage === (totalPages === null || totalPages === void 0 ? void 0 : totalPages.length),
      a11yLabel: "Go to Next Page",
      size: size,
      onClick: function onClick() {
        return setCurrentPage(parsedCurrentPage + 1);
      }
    }, /*#__PURE__*/React.createElement(ChevronRight, null)));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles.paginationContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.leftCol
  }, /*#__PURE__*/React.createElement(ItemsPerPage, null), /*#__PURE__*/React.createElement(TotalItemCount, null)), /*#__PURE__*/React.createElement(PaginationList, null));
};
Pagination.displayName = 'Pagination';

export { Pagination };
